/**
 * API Utility Module
 * Handles all HTTP requests to the backend with authentication and error handling
 */

import axios from 'axios';
import { apiErrorEvents, parseApiError } from './apiErrorHandler';
import { sessionEvents } from './sessionEvents';
import { refreshMeansSessionEnded } from './refreshFailure';
import { isUsableAccessToken, readAccessToken } from './accessToken';
import { isSuspectFailure, reportPossibleOutage } from './serverHealth';

// API Configuration
const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/api';
// Strip the trailing `/api` (or `/api/`) ONLY — a plain `.replace('/api','')`
// removes the first match, which corrupts an `api.` subdomain
// (`https://api.canira.io/api` -> `https:/.canira.io/api`). Anchored to the end.
const BACKEND_BASE = API_URL.replace(/\/api\/?$/, '');

/**
 * Resolves image URLs to full URLs
 * Handles both relative paths (/uploads/...) and full URLs (https://...)
 */
export const resolveImageUrl = (url) => {
  if (!url) return null;
  // If it's already a full URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // If it's a relative path, prepend the backend base URL
  if (url.startsWith('/')) {
    return `${BACKEND_BASE}${url}`;
  }
  return url;
};

// Storage keys (must match AuthContext + TeamContext)
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'canira_access_token',
  REFRESH_TOKEN: 'canira_refresh_token',
  CURRENT_TEAM_ID: 'canira_current_team_id',
};

// Reading the stored token lives in its own module — see utils/accessToken for
// why (this one is mocked by ~27 suites and pulls in axios). Re-exported here
// because plenty of callers already import their HTTP helpers from this file.
export { isUsableAccessToken, readAccessToken } from './accessToken';

// Create axios instance with default configuration
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
  withCredentials: true, // Send httpOnly cookies with every request
});

// Track if we're currently refreshing the token
let isRefreshing = false;
let failedQueue = [];

// One refresh attempt per page load for a caller carrying NO stored access
// token. See the 401 handler for why that case must still be attempted at all;
// this bounds what it costs. Module state, not storage — the whole point is
// that storage may be unavailable or empty. Cleared whenever a refresh
// succeeds, so a session that loses its token later gets a fresh attempt.
let anonymousRefreshTried = false;

// 429 rate-limit handling: suppress duplicate toasts and queue retries
let rateLimitedUntil = 0; // timestamp when the current rate-limit window expires
const RATE_LIMIT_RETRY_DELAY = 2000; // ms to wait before retrying a 429'd request
const RATE_LIMIT_TOAST_COOLDOWN = 5000; // only show one 429 toast per 5 seconds
let lastRateLimitToast = 0;

// Forget the stored session. Wrapped because Safari private mode throws on
// localStorage, and both callers run inside the 401 error handler where a
// second throw would replace the real failure with a storage one.
const clearStoredSession = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem('canira_user');
  } catch {
    // Nothing stored to clear, which is the state we wanted anyway.
  }
};

// Process queued requests after token refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor - add auth token, X-Team-Id, and handle FormData
api.interceptors.request.use(
  (config) => {
    const token = readAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Active-team scoping: TeamContext writes the current team id to
    // localStorage on every change. The backend `currentTeam` middleware
    // reads `X-Team-Id` and scopes routes (e.g. GET /api/webinars/my)
    // accordingly. We do this from localStorage instead of importing
    // TeamContext here to avoid the circular dependency (TeamContext
    // imports api). Skip the header on auth endpoints — those are
    // user-scoped and irrelevant to team context.
    //
    // Use startsWith (not includes) for precision: `includes('/auth/')`
    // would also match a hypothetical `/users/auth-shape` URL and skip
    // the header where it's actually wanted. Auth endpoints all live
    // under `/auth/...` so the prefix check is exact.
    try {
      const teamId = localStorage.getItem(STORAGE_KEYS.CURRENT_TEAM_ID);
      if (teamId && !config.url?.startsWith('/auth/')) {
        config.headers['X-Team-Id'] = teamId;
      }
    } catch {
      // localStorage unavailable (private mode quota, SSR) — skip silently.
    }

    // For FormData, remove Content-Type to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // No response / 502-504 might mean the server is down; ServerOutageGuard
    // confirms with /health before showing anything.
    if (isSuspectFailure(error)) reportPossibleOutage();

    // Handle 429 Too Many Requests: retry once after delay, suppress toast flood
    if (error.response?.status === 429 && !originalRequest._rateLimitRetry) {
      originalRequest._rateLimitRetry = true;
      const retryAfter = error.response.headers['retry-after'];
      const delay = retryAfter ? Math.min(parseInt(retryAfter, 10) * 1000, 10000) : RATE_LIMIT_RETRY_DELAY;
      rateLimitedUntil = Date.now() + delay;

      // Show at most one 429 toast per cooldown window
      const now = Date.now();
      if (now - lastRateLimitToast > RATE_LIMIT_TOAST_COOLDOWN) {
        lastRateLimitToast = now;
        apiErrorEvents.emit({
          title: 'Too Many Requests',
          message: 'Please wait a moment while we reconnect...',
          type: 'warning',
        });
      }

      // Wait then retry the request once
      await new Promise(resolve => setTimeout(resolve, delay));
      return api(originalRequest);
    }

    // If error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry auth endpoints
      if (originalRequest.url?.includes('/auth/')) {
        return Promise.reject(error);
      }

      // If the session was explicitly revoked (admin action, user logged out
      // elsewhere), skip refresh and force logout.
      //
      // Any other 401 still tries the refresh, because an expired ACCESS token
      // over a live session is the ordinary case and is exactly what refresh is
      // for. It is not a way around the session windows: /auth/refresh applies
      // the same idle and absolute checks (sessionService.checkRefreshAllowed),
      // so a session that has genuinely timed out is refused there and the
      // refresh-failure branch below ends it.
      const errorCode = error.response?.data?.code;
      if (errorCode === 'SESSION_REVOKED') {
        clearStoredSession();
        // Announce rather than navigate — see utils/sessionEvents. This
        // interceptor does not know whether the current page needs auth, and a
        // forced /login on a public page is how an emailed registration link
        // became a login wall for anyone with a stale session.
        sessionEvents.emitSessionLost('revoked');
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue the request while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // Refresh runs off the httpOnly refresh cookie (same-site), not a
      // localStorage token — so AN EMPTY localStorage MUST NOT SKIP IT.
      //
      // This used to return early when no stored token was present, reasoning
      // that such a 401 is anonymous. On Chrome that holds, because
      // localStorage persists. On SAFARI it does not: ITP deletes all
      // script-writable storage after 7 days without interaction with the
      // site, and the access cookie expires at 7 days too, so a host coming
      // back after a week had both gone while the 30-day refresh cookie was
      // still perfectly valid. The one credential the refresh does not need
      // was the one deciding whether it ran, and they were signed out and
      // told nothing. That is the whole Safari "kept getting logged out"
      // report.
      //
      // So: always attempt it. `anonymousRefreshTried` keeps the cost of a
      // genuinely anonymous visitor at ONE request per page load rather than
      // one per 401, which is what the early return was really buying.
      //
      // DELIBERATELY the raw read, not readAccessToken(). This asks "does the
      // user think they are signed in?", which a poisoned value still answers
      // yes to — and that is exactly what lets the refresh run and overwrite
      // it with a good token. Shape-checking here would skip the refresh for
      // the one case that needs it and leave the account stuck on a dead 401,
      // which is the bug utils/accessToken exists to end. Wrapped because
      // Safari private mode makes localStorage itself throw, and this sits
      // inside an error handler where a second throw loses the original.
      let hadStoredToken = false;
      try {
        hadStoredToken = !!localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      } catch {
        hadStoredToken = false;
      }

      if (!hadStoredToken) {
        if (anonymousRefreshTried) {
          isRefreshing = false;
          processQueue(error, null);
          return Promise.reject(error);
        }
        anonymousRefreshTried = true;
      }

      try {
        // Empty body — the backend reads canira_refresh_token from the cookie.
        const response = await axios.post(`${API_URL}/auth/refresh`, {}, { withCredentials: true });

        // `success: true` is not on its own proof that a token came back, and
        // this was the ONLY unguarded write of the access token in the app.
        // Writing an absent one stored the literal string "undefined", which
        // is truthy, so it was then sent as `Bearer undefined` on every later
        // request until something overwrote it — see isUsableAccessToken.
        // Treat a success without a usable token as a failed refresh so the
        // catch below decides what to do, rather than poisoning storage.
        if (response.data.success && isUsableAccessToken(response.data.accessToken)) {
          const { accessToken } = response.data;

          // A refresh worked, so this caller has a live session: let a later
          // storage wipe earn a fresh attempt rather than being capped by an
          // earlier anonymous one.
          anonymousRefreshTried = false;

          try {
            localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
          } catch {
            // Storage blocked (Safari private mode). The in-memory default
            // header below still carries the session for this page load.
          }

          api.defaults.headers.Authorization = `Bearer ${accessToken}`;
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;

          processQueue(null, accessToken);
          return api(originalRequest);
        }
        throw new Error('Refresh returned no usable access token');
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Only tear the session down when the refresh endpoint actually
        // REFUSED this session. A 429 (the app's own traffic used up the
        // per-IP budget), a 5xx or a dropped connection say nothing about
        // whether the refresh cookie is still good — and treating them as a
        // dead session signed hosts out mid-session and then told them their
        // session had expired. See utils/refreshFailure.
        if (refreshMeansSessionEnded(refreshError)) {
          clearStoredSession();
          // Announce it only for a caller that WAS carrying a token. With no
          // stored token this is the attempt added above, and the endpoint
          // answers 400 when no refresh cookie was sent — i.e. an ordinary
          // anonymous visitor on a public page. Announcing a lost session
          // there would be telling someone who never signed in that their
          // session ended.
          //
          // Same reasoning as SESSION_REVOKED above: clear the session and let
          // PrivateRoute decide. It redirects a protected route and carries
          // `from`, so the deep link survives — the hard navigation this
          // replaced discarded it.
          if (hadStoredToken) sessionEvents.emitSessionLost('refresh_failed');
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Emit error event for toast notifications.
    //
    // Suppressions:
    // - 401 — handled by the token refresh flow above
    // - 429 — handled by the rate-limit branch above
    // - FEATURE_NOT_AVAILABLE (plan-limit denials from checkCourseAccess,
    //   checkEvergreenAccess, etc.) — these should NEVER surface as global
    //   toasts. They may be produced by requests the user didn't initiate
    //   (e.g. a dashboard page prefetch) and result in confusing "Access
    //   Denied" notifications for actions the user never took. Features
    //   that DO want to prompt an upgrade in response to a plan-limit 403
    //   should catch the error locally and call showUpgradeToast()
    //   explicitly with the right context (see utils/toastHelpers).
    const errorCode = error.response?.data?.code;
    const isPlanLimitDenial = errorCode === 'FEATURE_NOT_AVAILABLE';
    if (
      error.response?.status !== 401 &&
      error.response?.status !== 429 &&
      !isPlanLimitDenial &&
      // Per-request opt-out: callers that expect and handle an error locally
      // (e.g. an ownership probe that redirects on 403) pass
      // { suppressGlobalError: true } so the expected failure doesn't surface
      // as a confusing global toast.
      !originalRequest?.suppressGlobalError
    ) {
      const parsed = parseApiError(error);
      if (parsed) {
        apiErrorEvents.emit(parsed);
      }
    }

    return Promise.reject(error);
  }
);

// ===========================================
// Authentication API
// ===========================================
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),

  login: (credentials) => api.post('/auth/login', credentials),

  logout: () => api.post('/auth/logout'),

  // Refresh via the httpOnly refresh cookie (sent automatically, same-site).
  // No refresh token is read from or sent by JS.
  refresh: () => api.post('/auth/refresh'),

  getProfile: () => api.get('/auth/me'),

  updateProfile: (profileData) => api.put('/auth/profile', profileData),

  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),

  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),

  resetPassword: (token, newPassword) =>
    api.post('/auth/reset-password', { token, newPassword }),
  // Founding checkout return page (/welcome/founding).
  foundingClaimStatus: (sessionId) => api.post('/auth/founding-claim/status', { sessionId }),
  foundingClaim: (sessionId, password) => api.post('/auth/founding-claim', { sessionId, password }),

  // ===========================================
  // Two-factor authentication (TOTP) — GH #776
  // ===========================================
  twoFactorSetup: () => api.post('/auth/2fa/setup'),

  twoFactorVerify: (code) => api.post('/auth/2fa/verify', { code }),

  twoFactorDisable: (currentPassword, code) =>
    api.post('/auth/2fa/disable', { currentPassword, code }),

  twoFactorRegenerate: (code) =>
    api.post('/auth/2fa/recovery-codes/regenerate', { code }),

  // Completes login after the server returned twoFactorRequired: true.
  // Accepts a TOTP code OR a recovery code.
  twoFactorChallenge: (twoFactorChallengeToken, code) =>
    api.post('/auth/2fa/challenge', { twoFactorChallengeToken, code }),
};

// ===========================================
// Webinar API
// ===========================================
export const webinarAPI = {
  getAll: (params = {}) => api.get('/webinars', { params }),

  getById: (id) => api.get(`/webinars/${id}`),

  // Public lookup by slug — used by the /workshop custom landing page to
  // resolve the host-created workshop Webinar without hardcoding its UUID.
  // Returns 404 cleanly when the slug isn't registered yet.
  getBySlug: (slug) => api.get(`/webinars/slug/${slug}`),
  // Side-effect-free count for live social-proof polling — does NOT bump
  // viewCount (unlike getBySlug), so polling can't inflate Page Views.
  getRegistrationCountBySlug: (slug) => api.get(`/webinars/slug/${slug}/registration-count`),

  create: (webinarData) => api.post('/webinars', webinarData),

  // Alias for create with config support (for file uploads with multipart/form-data)
  createWebinar: (webinarData, config = {}) => api.post('/webinars', webinarData, config),

  update: (id, webinarData) => api.put(`/webinars/${id}`, webinarData),

  delete: (id) => api.delete(`/webinars/${id}`),

  // Alias for delete
  deleteWebinar: (id) => api.delete(`/webinars/${id}`),

  // Bulk delete all webinars for the authenticated host
  deleteAll: () => api.delete('/webinars'),

  // Toggle webinar visibility (public/private)
  setVisibility: (id, visibility) => api.patch(`/webinars/${id}/visibility`, { visibility }),

  // Admin curation override for the global /webinars explore feed.
  // override: 'force_show' | 'force_hide' | null (clears override).
  adminFeature: (id, override) => api.patch(`/webinars/${id}/admin-feature`, { override }),

  // Admin curation for the CANIRA TV (Roku) channel. featured: true|false.
  rokuFeature: (id, featured) => api.patch(`/webinars/${id}/roku-feature`, { featured }),
  // Host-side opt-in / opt-out for the CANIRA TV channel. featured: true|false.
  // The webinar must be visibility='public' to opt IN; opting OUT is always
  // allowed. Replaces the silent auto-feature-at-go-live behaviour.
  rokuSelfToggle: (id, featured) => api.patch(`/webinars/${id}/roku-self-toggle`, { featured }),
  // Spotlight pin — promote a Roku-featured replay to the hero banner. hero: bool.
  rokuHero: (id, hero) => api.patch(`/webinars/${id}/roku-hero`, { hero }),
  // Set or clear the 16:9 branded artwork URL for CANIRA TV display.
  // Pass null/empty string to clear and fall back to thumbnailUrl.
  rokuArtwork: (id, brandedArtworkUrl) =>
    api.patch(`/webinars/${id}/roku-artwork`, { brandedArtworkUrl }),

  // Admin dismisses a pending host request without featuring the webinar.
  // Clears rokuRequestedAt; leaves featuredOnRoku unchanged.
  rokuDismissRequest: (id) => api.patch(`/webinars/${id}/roku-dismiss-request`, {}),

  // Host requests their webinar be featured on CANIRA TV (Roku).
  requestRoku: (id) => api.post(`/webinars/${id}/request-roku`, {}),

  // Per-webinar opt-out for broadcasting LIVE on CANIRA TV. enabled: true|false.
  // Host (own webinar) or admin. Defaults true; pass false to keep a specific
  // live off the channel. Distinct from rokuFeature (recorded replay curation).
  rokuLive: (id, enabled) => api.patch(`/webinars/${id}/roku-live`, { enabled }),

  // Admin moderation list — every webinar with visibility='public',
  // enriched with host plan info + effectiveStatus for at-a-glance
  // moderation. Admin-only.
  adminPublicFeed: () => api.get('/webinars/admin/public-feed'),

  // Publish/unpublish webinar (changes status to scheduled/draft)
  publish: (id) => api.patch(`/webinars/${id}/status`, { status: 'scheduled' }),
  unpublish: (id) => api.patch(`/webinars/${id}/status`, { status: 'draft' }),

  // Get only the current authenticated user's webinars
  getMyWebinars: (params = {}) => api.get('/webinars/my', { params }),

  // Get webinars by host ID
  getByHostId: (hostId) => api.get('/webinars', { params: { hostId } }),

  // Sessions
  getSessions: (webinarId) => api.get(`/webinars/${webinarId}/sessions`),

  createSession: (webinarId, sessionData) =>
    api.post(`/webinars/${webinarId}/sessions`, sessionData),

  updateSession: (webinarId, sessionId, sessionData) =>
    api.put(`/webinars/${webinarId}/sessions/${sessionId}`, sessionData),

  deleteSession: (webinarId, sessionId) =>
    api.delete(`/webinars/${webinarId}/sessions/${sessionId}`),

  // Registration
  register: (webinarId, registrationData) =>
    api.post(`/webinars/${webinarId}/register`, registrationData),

  getRegistrations: (webinarId, params = {}) =>
    api.get(`/webinars/${webinarId}/registrations`, { params }),

  removeRegistration: (webinarId, registrationId) =>
    api.delete(`/webinars/${webinarId}/registrations/${registrationId}`),

  // The host's own attendance verdict — see the route for the rules.
  setRegistrationAttendance: (webinarId, registrationId, attended) =>
    api.patch(`/webinars/${webinarId}/registrations/${registrationId}/attendance`, { attended }),

  // Status
  updateStatus: (webinarId, status) =>
    api.patch(`/webinars/${webinarId}/status`, { status }),

  // Analytics
  getAnalytics: (webinarId) => api.get(`/webinars/${webinarId}/analytics`),
};

// ===========================================
// CANIRA TV (Roku) courses — admin curation
// Courses appear as "shows" (series → seasons → episodes) on the channel.
// ===========================================
// Roku auto-approve toggles (admin only). When a flag is on, host
// "Request on CANIRA TV" calls immediately feature the content instead of
// landing in the moderation queue.
export const rokuSettingsAPI = {
  get: () => api.get('/admin/roku-settings'),
  update: (settings) => api.patch('/admin/roku-settings', settings),
};

export const rokuCoursesAPI = {
  // Admin list of free, published, public courses with their featured-on-Roku state.
  adminList: () => api.get('/courses/admin/roku-list'),
  // Admin toggle: feature/unfeature a course on Roku.
  feature: (id, featured) => api.patch(`/courses/${id}/roku-feature`, { featured }),
  // Admin tri-state CANIRA TV override: 'force_show' | 'force_hide' | null.
  override: (id, override) => api.patch(`/courses/${id}/roku-override`, { override }),
  // Admin "Spotlight" pin for the CANIRA TV hero banner (true/false).
  hero: (id, hero) => api.patch(`/courses/${id}/roku-hero`, { hero }),
  // Admin set/clear the 16:9 branded TV artwork (HTTPS URL or null).
  artwork: (id, brandedArtworkUrl) => api.patch(`/courses/${id}/roku-artwork`, { brandedArtworkUrl }),
  // Host requests their course be featured as a show on CANIRA TV.
  request: (id) => api.post(`/courses/${id}/request-roku`, {}),
};

// Admin CRUD for the CANIRA TV sponsors list. Drives the rotating
// "PRESENTED BY" line on the hero spot + the sponsor row in the
// channel's carousel. Mutating calls invalidate the Roku feed cache
// server-side so the channel picks up changes on its next refresh.
export const sponsorsAPI = {
  list: () => api.get('/admin/sponsors'),
  create: (payload) => api.post('/admin/sponsors', payload),
  update: (id, payload) => api.patch(`/admin/sponsors/${id}`, payload),
  remove: (id) => api.delete(`/admin/sponsors/${id}`),
};

// Ticker items - admin-curated text snippets that scroll across the
// bottom of the CANIRA TV channel during live broadcasts. Same admin
// CRUD shape as sponsors. Mutating calls invalidate the Roku feed
// cache server-side so the channel picks up changes on the next refresh.
export const tickerItemsAPI = {
  list: () => api.get('/admin/ticker-items'),
  create: (payload) => api.post('/admin/ticker-items', payload),
  update: (id, payload) => api.patch(`/admin/ticker-items/${id}`, payload),
  remove: (id) => api.delete(`/admin/ticker-items/${id}`),
};

// Admin overview of CANIRA TV host approvals (Phase 1). The list endpoint
// returns only users with `canBroadcastOnCaniraTV=true`. Revoke piggybacks
// on the existing per-user POST route from PR #1294.
export const caniraTVHostsAPI = {
  list: () => api.get('/admin/canira-tv-hosts'),
  revoke: (userId) => api.post(`/admin/users/${userId}/canira-tv-revoke`, {}),
};

// Admin curation of uploaded videos on the global CANIRA TV (Roku) channel.
export const mediaRokuAPI = {
  // Search, sort and paging are server-side: the list is far longer than one
  // page, and filtering a page in the browser cannot find what was never sent.
  adminList: (params) => api.get('/media-library/admin/roku-list', { params }),
  feedState: () => api.get('/admin/roku-feed-state'),
  feature: (id, featured) => api.patch(`/media-library/${id}/roku-feature`, { featured }),
  // Host-side opt-in / opt-out for an uploaded video. Owner or admin can
  // toggle. Since #1730 opt-IN is admin-only; owners use request() below.
  selfToggle: (id, featured) => api.patch(`/media-library/${id}/roku-self-toggle`, { featured }),
  // Host asks an admin to feature the video on CANIRA TV (admin approves via feature()).
  request: (id) => api.post(`/media-library/${id}/request-roku`),
  // Admin moderation queue: pending video requests awaiting approval.
  adminRequests: () => api.get('/media-library/admin/roku-requests'),
  hero: (id, hero) => api.patch(`/media-library/${id}/roku-hero`, { hero }),
  // Import a video into the library from a direct URL (Cloudflare ingests it
  // server-side — good for large/already-hosted videos). Lands as 'processing'.
  importUrl: (url, name) => api.post('/media-library/assets/from-url', { url, name }),
};

// Super Admin provisioning of white-label Roku channels.
export const rokuChannelsAdminAPI = {
  list: () => api.get('/admin/roku-channels'),
  create: (payload) => api.post('/admin/roku-channels', payload),
  update: (id, payload) => api.patch(`/admin/roku-channels/${id}`, payload),
  remove: (id, hard = false) => api.delete(`/admin/roku-channels/${id}${hard ? '?hard=true' : ''}`),
};

// Team self-serve management of THEIR white-label Roku channel. Team-scoped
// (teamId in the path); the request interceptor also sends X-Team-Id.
export const rokuChannelAPI = {
  get: (teamId) => api.get(`/teams/${teamId}/roku-channel`),
  updateBranding: (teamId, payload) => api.patch(`/teams/${teamId}/roku-channel/branding`, payload),
  availableContent: (teamId) => api.get(`/teams/${teamId}/roku-channel/content/available`),
  listContent: (teamId) => api.get(`/teams/${teamId}/roku-channel/content`),
  addContent: (teamId, payload) => api.post(`/teams/${teamId}/roku-channel/content`, payload),
  removeContent: (teamId, itemId) => api.delete(`/teams/${teamId}/roku-channel/content/${itemId}`),
  reorderContent: (teamId, items) => api.patch(`/teams/${teamId}/roku-channel/content/reorder`, { items }),
  pinContent: (teamId, itemId, heroPinned) => api.patch(`/teams/${teamId}/roku-channel/content/${itemId}`, { heroPinned }),
  listSponsors: (teamId) => api.get(`/teams/${teamId}/roku-channel/sponsors`),
  createSponsor: (teamId, payload) => api.post(`/teams/${teamId}/roku-channel/sponsors`, payload),
  updateSponsor: (teamId, id, payload) => api.patch(`/teams/${teamId}/roku-channel/sponsors/${id}`, payload),
  removeSponsor: (teamId, id) => api.delete(`/teams/${teamId}/roku-channel/sponsors/${id}`),
  listTicker: (teamId) => api.get(`/teams/${teamId}/roku-channel/ticker`),
  createTicker: (teamId, payload) => api.post(`/teams/${teamId}/roku-channel/ticker`, payload),
  updateTicker: (teamId, id, payload) => api.patch(`/teams/${teamId}/roku-channel/ticker/${id}`, payload),
  removeTicker: (teamId, id) => api.delete(`/teams/${teamId}/roku-channel/ticker/${id}`),
};

// ===========================================
// Contacts API (Unified cross-webinar CRM)
// ===========================================
export const contactsAPI = {
  getAll: (params = {}) => api.get('/contacts', { params }),
  getStats: () => api.get('/contacts/stats'),
  getExport: (params = {}) => api.get('/contacts/export', { params }),
};

// ===========================================
// Viewing Links API (Personalized guest viewing links)
// ===========================================
export const viewingLinkAPI = {
  // Create a single viewing link
  create: (data) => api.post('/viewing-links', data),

  // Create multiple viewing links at once
  createBulk: (data) => api.post('/viewing-links/bulk', data),

  // List all viewing links for a webinar
  getByWebinar: (webinarId, params = {}) =>
    api.get(`/viewing-links/webinar/${webinarId}`, { params }),

  // Validate a viewing link token (public, no auth required)
  validate: (token) => api.get(`/viewing-links/validate/${token}`),

  // Revoke a viewing link
  revoke: (id) => api.patch(`/viewing-links/${id}/revoke`),

  // Delete a viewing link
  delete: (id) => api.delete(`/viewing-links/${id}`),
};

// ===========================================
// Podcast API
// ===========================================

// Audio from a Media Library video runs in the background on the server
// (long recordings take minutes). Poll its status until it's done. Resolves
// with { data: { success, episode, mediaAsset } } — the same shape the old
// one-shot request returned — or throws with the server's friendly message.
// onStatus({ status, held, holdMessage }) reports progress for the UI.
// shouldStop() → true ends the wait early (resolves null), e.g. on unmount.
const waitForAudioExtract = async (podcastId, episodeId, { onStatus, shouldStop, intervalMs = 4000 } = {}) => {
  const deadline = Date.now() + 75 * 60 * 1000;
  let networkErrors = 0;
  while (Date.now() < deadline) {
    if (shouldStop && shouldStop()) return null;
    let body;
    try {
      const resp = await api.get(
        `/podcasts/${podcastId}/episodes/${episodeId}/extract-from-library/status`,
        { suppressGlobalError: true }
      );
      body = resp.data || {};
      networkErrors = 0;
    } catch (err) {
      // A blip while polling isn't a failed extraction — the job carries on
      // server-side. Give up only if the server stays unreachable.
      networkErrors += 1;
      if ((err.response && err.response.status < 500) || networkErrors >= 4) throw err;
      await new Promise((r) => setTimeout(r, intervalMs));
      continue;
    }
    if (onStatus) onStatus(body);
    if (body.status === 'ready') {
      return { data: { success: true, episode: body.episode, mediaAsset: body.mediaAsset } };
    }
    if (body.status === 'error' || body.status === 'idle') {
      const message = body.error || 'Audio extraction did not complete. Please try again.';
      const err = new Error(message);
      err.response = { data: { success: false, error: message } };
      throw err;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error('Audio extraction is taking longer than expected. It will keep going — check back on this episode shortly.');
};

// Start the background extraction (202). Does not wait for it.
const startExtractFromLibrary = (podcastId, episodeId, assetId) =>
  api.post(`/podcasts/${podcastId}/episodes/${episodeId}/extract-from-library`, { assetId }, { timeout: 120000 });

export const podcastAPI = {
  // Shows
  getAll: (params = {}) => api.get('/podcasts', { params }),
  getById: (id) => api.get(`/podcasts/${id}`),
  create: (data) => api.post('/podcasts', data),
  update: (id, data) => api.put(`/podcasts/${id}`, data),
  delete: (id) => api.delete(`/podcasts/${id}`),

  // Episodes
  getEpisodes: (podcastId, params = {}) => api.get(`/podcasts/${podcastId}/episodes`, { params }),
  getEpisode: (podcastId, episodeId) => api.get(`/podcasts/${podcastId}/episodes/${episodeId}`),
  createEpisode: (podcastId, data) => api.post(`/podcasts/${podcastId}/episodes`, data),
  updateEpisode: (podcastId, episodeId, data) => api.put(`/podcasts/${podcastId}/episodes/${episodeId}`, data),
  deleteEpisode: (podcastId, episodeId) => api.delete(`/podcasts/${podcastId}/episodes/${episodeId}`),
  publishEpisode: (podcastId, episodeId) => api.patch(`/podcasts/${podcastId}/episodes/${episodeId}/publish`),
  unpublishEpisode: (podcastId, episodeId) => api.patch(`/podcasts/${podcastId}/episodes/${episodeId}/unpublish`),
  removeEpisodeAudio: (podcastId, episodeId) => api.delete(`/podcasts/${podcastId}/episodes/${episodeId}/audio`),

  // Audio upload (extended timeout for large files up to 500MB)
  uploadAudio: (podcastId, episodeId, formData, config = {}) =>
    api.post(`/podcasts/${podcastId}/episodes/${episodeId}/upload`, formData, { timeout: 600000, ...config }),

  // Import video (extract audio from video file — extended timeout for upload + FFmpeg extraction)
  importVideo: (podcastId, episodeId, formData, config = {}) =>
    api.post(`/podcasts/${podcastId}/episodes/${episodeId}/import-video`, formData, { timeout: 900000, ...config }),

  // Link existing audio from Media Library to episode
  linkAudio: (podcastId, episodeId, assetId) =>
    api.post(`/podcasts/${podcastId}/episodes/${episodeId}/link-audio`, { assetId }),

  // Extract audio from a video asset in the Media Library. The server queues
  // it (202) and this polls until the audio is on the episode.
  extractFromLibrary: async (podcastId, episodeId, assetId, { onStatus } = {}) => {
    const resp = await startExtractFromLibrary(podcastId, episodeId, assetId);
    // A server still on the old one-shot route answers with the result.
    if (resp.status !== 202 && resp.data?.episode) return resp;
    if (onStatus) onStatus(resp.data || {});
    return waitForAudioExtract(podcastId, episodeId, { onStatus });
  },
  startExtractFromLibrary,
  waitForAudioExtract,

  // Transcript. Transcription runs in the background (long episodes exceed
  // request/CDN timeouts), so we kick it off then poll the episode for
  // metadata.transcriptStatus. Resolves with the episode GET response once
  // ready (so callers can read response.data.episode.transcript as before).
  transcribeEpisode: async (podcastId, episodeId) => {
    await api.post(`/podcasts/${podcastId}/episodes/${episodeId}/transcribe`, null);
    const deadline = Date.now() + 12 * 60 * 1000; // give up after 12 min
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 4000));
      const resp = await api.get(`/podcasts/${podcastId}/episodes/${episodeId}`);
      const ep = resp.data?.episode || resp.data || {};
      const status = ep.metadata?.transcriptStatus;
      if (status === 'ready') return resp;
      if (status === 'error') {
        throw new Error(ep.metadata?.transcriptError || 'Transcription failed');
      }
      // Older episodes (pre-status) or fallback: if a transcript appeared and
      // we're no longer 'processing', treat as done.
      if (!status && ep.transcript) return resp;
    }
    throw new Error('Transcription is taking longer than expected. Check back shortly.');
  },

  // ---- Audio editor: reusable clip library (intros / outros / sponsor spots) ----
  getAudioLibrary: (kind) =>
    api.get('/podcasts/audio-library', { params: kind ? { kind } : {} }),
  uploadAudioClip: (formData, config = {}) =>
    api.post('/podcasts/audio-library', formData, { timeout: 600000, ...config }),
  deleteAudioClip: (clipId) =>
    api.delete(`/podcasts/audio-library/${clipId}`),

  // ---- Audio editor: per-episode edit decision list + render ----
  saveAudioEdit: (podcastId, episodeId, edl) =>
    api.put(`/podcasts/${podcastId}/episodes/${episodeId}/audio-edit`, edl),
  renderEpisodeAudio: (podcastId, episodeId, edl) =>
    api.post(`/podcasts/${podcastId}/episodes/${episodeId}/render`, edl || {}),
  getRenderStatus: (podcastId, episodeId) =>
    api.get(`/podcasts/${podcastId}/episodes/${episodeId}/render/status`),
  revertEpisodeAudio: (podcastId, episodeId) =>
    api.post(`/podcasts/${podcastId}/episodes/${episodeId}/revert-audio`),

  // Cover image
  uploadCover: (podcastId, formData, config = {}) =>
    api.post(`/podcasts/${podcastId}/cover`, formData, config),

  // Public
  getPublicAll: (params = {}) => api.get('/podcasts/public', { params }),
  // PodcastPublicPage renders a full inline error screen for every failure
  // here (private show, unknown slug, unreachable backend) and retries the
  // transient ones. A global toast on top of that is duplicate messaging —
  // and one toast per retry attempt, since the notifier only dedupes within
  // a 3s window. Encode the slug: useParams() hands back a decoded segment.
  getPublicBySlug: (slug) =>
    api.get(`/podcasts/public/${encodeURIComponent(slug)}`, { suppressGlobalError: true }),

  // Analytics
  trackEvent: (data) => api.post('/podcasts/public/analytics/track', data),
  getAnalytics: (podcastId, params = {}) => api.get(`/podcasts/${podcastId}/analytics`, { params }),
};

// ===========================================
// Chat API
// ===========================================
export const chatAPI = {
  getMessages: (webinarId, params = {}) =>
    api.get(`/chat/webinars/${webinarId}`, { params }),

  saveMessage: (messageData) => api.post('/chat', messageData),

  deleteMessage: (messageId) => api.delete(`/chat/${messageId}`),

  getAutomatedMessages: (webinarId) =>
    api.get(`/chat/automated/webinars/${webinarId}`),

  createAutomatedMessage: (webinarId, messageData) =>
    api.post(`/chat/automated/webinars/${webinarId}`, messageData),
};

// ===========================================
// Poll API
// ===========================================
export const pollAPI = {
  getPolls: (webinarId) => api.get(`/polls/webinars/${webinarId}`),

  getPoll: (pollId) => api.get(`/polls/${pollId}`),

  createPoll: (pollData) => api.post('/polls', pollData),

  updatePoll: (pollId, pollData) => api.put(`/polls/${pollId}`, pollData),

  deletePoll: (pollId) => api.delete(`/polls/${pollId}`),

  activatePoll: (pollId, isActive) =>
    api.patch(`/polls/${pollId}/status`, { isActive }),

  submitResponse: (pollId, optionId) =>
    api.post(`/polls/${pollId}/responses`, { optionId }),

  getResults: (pollId) => api.get(`/polls/${pollId}/results`),
};

// ===========================================
// Q&A API
// ===========================================
export const qaAPI = {
  getQuestions: (webinarId, params = {}) =>
    api.get(`/qa/webinars/${webinarId}`, { params }),

  submitQuestion: (webinarId, question) =>
    api.post(`/qa/webinars/${webinarId}`, { question }),

  answerQuestion: (questionId, answer) =>
    api.post(`/qa/${questionId}/answer`, { answer }),

  updateQuestionStatus: (questionId, status) =>
    api.patch(`/qa/${questionId}/status`, { status }),

  deleteQuestion: (questionId) => api.delete(`/qa/${questionId}`),
};

// ===========================================
// Resource API
// ===========================================
export const resourceAPI = {
  getResources: (webinarId) => api.get(`/resources/webinars/${webinarId}`),

  getResource: (resourceId) => api.get(`/resources/${resourceId}`),

  createResource: (resourceData) => api.post('/resources', resourceData),

  updateResource: (resourceId, resourceData) =>
    api.put(`/resources/${resourceId}`, resourceData),

  deleteResource: (resourceId) => api.delete(`/resources/${resourceId}`),

  uploadResource: (webinarId, formData) =>
    api.post(`/resources/webinars/${webinarId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// ===========================================
// Analytics API
// ===========================================
export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),

  getWebinarAnalytics: (webinarId) => api.get(`/analytics/webinars/${webinarId}`),

  getDropoff: (webinarId) => api.get(`/analytics/webinars/${webinarId}/dropoff`),
};

// ===========================================
// User API
// ===========================================
export const userAPI = {
  getProfile: () => api.get('/users/me'),

  updateProfile: (profileData) => api.put('/users/me', profileData),

  getWebinars: () => api.get('/users/me/webinars'),

  getRegistrations: () => api.get('/users/me/registrations'),
};

// ===========================================
// Newsletter API
// ===========================================
export const newsletterAPI = {
  subscribe: (email, preferences = {}) =>
    api.post('/newsletter/subscribe', { email, ...preferences }),

  unsubscribe: (email, token) =>
    api.post('/newsletter/unsubscribe', { email, token }),

  updatePreferences: (email, preferences) =>
    api.put('/newsletter/preferences', { email, preferences }),
};

// ===========================================
// Evergreen API
// ===========================================
export const evergreenAPI = {
  // Get evergreen schedule for a webinar
  getSchedule: (webinarId) => api.get(`/evergreen/schedules/${webinarId}`),

  // Save/update evergreen schedule (POST to create, PUT to update)
  saveSchedule: (webinarId, scheduleData) =>
    api.post('/evergreen/schedules', { webinarId, ...scheduleData }),

  // Update an existing schedule by ID
  updateSchedule: (scheduleId, scheduleData) =>
    api.put(`/evergreen/schedules/${scheduleId}`, scheduleData),

  // Get available times for a webinar
  getAvailableTimes: (webinarId, count = 5) =>
    api.get(`/evergreen/sessions/${webinarId}/available-times`, { params: { count } }),

  // Quick launch a session
  quickLaunchSession: (webinarId, isPreview = false) =>
    api.post('/evergreen/sessions/quick-launch', { webinarId, isPreview }),

  // Get evergreen stats
  getStats: () => api.get('/evergreen/stats'),

  // Get webinar-specific evergreen analytics
  getAnalytics: (webinarId) => api.get(`/evergreen/analytics/${webinarId}`),

  // Toggle evergreen schedule active status
  toggleStatus: (webinarId, isActive) =>
    api.get(`/evergreen/schedules/${webinarId}`).then(res => {
      const scheduleId = res.data?.data?.id;
      if (scheduleId) {
        return api.put(`/evergreen/schedules/${scheduleId}`, { isActive });
      }
      throw new Error('Schedule not found');
    }),
};

// ===========================================
// Content Scheduler API (Content Calendar / Publishing Scheduler)
// All routes 404 when the backend CONTENT_SCHEDULER_ENABLED flag is off.
// ===========================================
// "Your week": clips, posts and follow-up emails prepared after a session.
export const mobileAppAPI = {
  // view: 'card' only from the dashboard card, so a shown card is counted.
  status: ({ view } = {}) => api.get('/mobile-app/status', { suppressGlobalError: true, params: view ? { view } : undefined }),
  dismiss: () => api.post('/mobile-app/dismiss', {}, { suppressGlobalError: true }),
  sendLink: () => api.post('/mobile-app/send-link', {}, { suppressGlobalError: true }),
};

export const yourWeekAPI = {
  webinarStatus: (webinarId) => api.get(`/your-week/webinars/${webinarId}`),
  setEnabled: (webinarId, enabled) => api.put(`/your-week/webinars/${webinarId}`, { enabled }),
  runNow: (webinarId) => api.post(`/your-week/webinars/${webinarId}/run`),
  promptSeen: () => api.post('/your-week/prompt-seen'),
  saveSettings: (data) => api.put('/your-week/settings', data),
  listRuns: () => api.get('/your-week/runs'),
  waiting: () => api.get('/your-week/waiting', { suppressGlobalError: true }),
  getRun: (runId) => api.get(`/your-week/runs/${runId}`),
  approveAll: (runId) => api.post(`/your-week/runs/${runId}/approve-all`),
  skip: (runId) => api.post(`/your-week/runs/${runId}/skip`),
};

export const schedulerAPI = {
  // Aggregated calendar feed (webinars + podcasts + bookings + scheduled posts)
  getCalendar: (from, to) => api.get('/scheduler/calendar', { params: { from, to } }),

  // List this host's scheduled posts (optionally filtered by status)
  listPosts: (status) => api.get('/scheduler/posts', { params: status ? { status } : {} }),

  // Schedule a clip to publish:
  // { type:'clip', channel, publishAt, payload:{ cloudflareUid|clipUrl, clipId?, caption?, hashtags? }, webinarId? }
  createPost: (data) => api.post('/scheduler/posts', data),

  // Bulk-schedule from friendly rows (CSV import). { posts: [ {type, channel, publishAt, caption?, hashtags?, title?, media?, text?, link?} ] }
  bulkImport: (posts) => api.post('/scheduler/posts/bulk', { posts }),

  // Reschedule / edit while still scheduled
  updatePost: (id, data) => api.patch(`/scheduler/posts/${id}`, data),

  // Cancel a scheduled post
  cancelPost: (id) => api.delete(`/scheduler/posts/${id}`),

  // Posts a connected assistant suggested, waiting for the host's approval
  listProposals: () => api.get('/scheduler/proposals'),
  // Approve one (optionally with edited { payload, publishAt }); it is then booked
  approvePost: (id, data = {}) => api.post(`/scheduler/posts/${id}/approve`, data),
  rejectPost: (id) => api.post(`/scheduler/posts/${id}/reject`),
  // Canira's own suggestions: { enabled, facebookPageId }, and "Suggest now"
  getSuggestionSettings: () => api.get('/scheduler/suggestions/settings'),
  saveSuggestionSettings: (data) => api.put('/scheduler/suggestions/settings', data),
  runSuggestions: () => api.post('/scheduler/suggestions/run'),

  // Which social platforms the host has connected (for the channel picker)
  getConnectedAccounts: () => api.get('/social/connected-accounts'),
};

// ===========================================
// Social API — publishing targets
// ===========================================
export const socialAPI = {
  // The host's connected Facebook Pages. Needed wherever a Facebook post has
  // to name its destination: a text post or a clip goes to a PAGE, never a
  // personal profile, and "the first one" is not something to show a host as
  // if it were their choice.
  getFacebookPages: () => api.get('/social/facebook/pages'),
};

// ===========================================
// Teams API
// ===========================================
export const teamsAPI = {
  // Get all teams the user belongs to
  getTeams: () => api.get('/teams'),

  // Create a new team
  createTeam: (teamData) => api.post('/teams', teamData),

  // Get team details
  getTeam: (teamId) => api.get(`/teams/${teamId}`),

  // Update team settings
  updateTeam: (teamId, updates) => api.put(`/teams/${teamId}`, updates),

  // Delete team
  deleteTeam: (teamId) => api.delete(`/teams/${teamId}`),

  // Get team members
  getMembers: (teamId) => api.get(`/teams/${teamId}/members`),

  // Update team member
  updateMember: (teamId, memberId, updates) =>
    api.put(`/teams/${teamId}/members/${memberId}`, updates),

  // Remove team member
  removeMember: (teamId, memberId) =>
    api.delete(`/teams/${teamId}/members/${memberId}`),

  // Get team invitations
  getInvitations: (teamId) => api.get(`/teams/${teamId}/invitations`),

  // Send invitation
  sendInvitation: (teamId, invitationData) =>
    api.post(`/teams/${teamId}/invitations`, invitationData),

  // Revoke invitation
  revokeInvitation: (teamId, invitationId) =>
    api.delete(`/teams/${teamId}/invitations/${invitationId}`),

  // Get my pending invitations
  getMyInvitations: () => api.get('/teams/my-invitations'),

  // Get invitation details by token
  getInvitationByToken: (token) => api.get(`/teams/invitations/${token}`),

  // Accept invitation
  acceptInvitation: (token) => api.post(`/teams/invitations/${token}/accept`),

  // Decline invitation
  declineInvitation: (token) => api.post(`/teams/invitations/${token}/decline`),

  // Reusable, email-less join code
  generateJoinCode: (teamId) => api.post(`/teams/${teamId}/join-code`),
  disableJoinCode: (teamId) => api.delete(`/teams/${teamId}/join-code`),
  previewJoinCode: (code) => api.get(`/teams/join-code/${code}`),
  joinByCode: (code) => api.post('/teams/join-by-code', { code }),

  // Transfer ownership
  transferOwnership: (teamId, newOwnerId) =>
    api.post(`/teams/${teamId}/transfer-ownership`, { newOwnerId }),
};

// ===========================================
// Media Library API
// ===========================================
export const mediaLibraryAPI = {
  extractAudio: (assetId) => api.post(`/media-library/assets/${assetId}/extract-audio`, null, { timeout: 60000 }),
  getExtractionStatus: (assetId) => api.get(`/media-library/assets/${assetId}/extract-audio/status`),
  // Upload one file to the Media Library (same endpoint as MediaUploader).
  // -> { asset }. `onProgress(percent)` reports the upload.
  uploadAsset: (file, { type = 'video', name, onProgress } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    formData.append('name', name || String(file.name || 'Video').replace(/\.[^/.]+$/, ''));
    return api.post('/media-library/assets/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      onUploadProgress: (e) => {
        if (onProgress && e && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
  },
};

// ===========================================
// Creator Courses API
// ===========================================

/**
 * Fire-and-forget progress save that survives the page closing (pagehide,
 * tab hidden, leaving the player). fetch keepalive is preferred because it can
 * carry the Authorization header the progress route reads; sendBeacon (which
 * can't send headers) is the fallback, and still works for guest learners
 * whose access token travels in the body. Returns whether anything was sent.
 */
export function sendCourseProgressKeepalive(courseId, data) {
  const url = `${API_URL}/courses/${courseId}/progress`;
  const body = JSON.stringify(data || {});
  try {
    if (typeof fetch === 'function') {
      const headers = { 'Content-Type': 'application/json' };
      let token = null;
      try { token = readAccessToken(); } catch { /* storage blocked */ }
      if (token) headers.Authorization = `Bearer ${token}`;
      const p = fetch(url, { method: 'POST', keepalive: true, credentials: 'include', headers, body });
      if (p && typeof p.catch === 'function') p.catch(() => {});
      return true;
    }
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      return navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    }
  } catch { /* best effort only */ }
  return false;
}

export const courseAPI = {
  // Creator CRUD
  getAll: (params = {}) => api.get('/courses', { params }),
  getById: (id, config) => api.get(`/courses/${id}`, config),
  create: (data) => api.post('/courses', data),
  // Create a full course (draft + modules + lessons) from an AI Builder outline
  createFromOutline: (data) => api.post('/courses/from-outline', data),
  // Replay → course. `recordingChapters` proposes the split and creates
  // nothing; it may report that a transcription was queued, in which case poll
  // it again. Commit the reviewed chapters with `createFromRecording`.
  recordingChapters: (data) => api.post('/courses/recording-chapters', data),
  createFromRecording: (data) => api.post('/courses/from-recording', data),
  // Course wizard. `wizardOutline` proposes the plan and outline (creates
  // nothing); `wizardBuild` writes every lesson and creates the draft, which
  // takes a few minutes — hence the long timeout.
  wizardOutline: (data) => api.post('/courses/ai/outline', data, { timeout: 90000 }),
  wizardBuild: (data) => api.post('/courses/ai/build', data, { timeout: 480000 }),
  // Public preview clip — a short standalone render, so the course page never
  // has to serve the source recording to anonymous visitors.
  createPreviewClip: (id, lessonId, startOffset = 0) => api.post(`/courses/${id}/preview-clip`, { lessonId, startOffset }),
  getPreviewClip: (id) => api.get(`/courses/${id}/preview-clip`),
  // Stop a preview still building (the live one stays), or turn it off.
  cancelPreviewClip: (id) => api.delete(`/courses/${id}/preview-clip/pending`),
  removePreviewClip: (id) => api.delete(`/courses/${id}/preview-clip`),
  // Cut lessons that play part of one long recording into their own videos.
  requestLessonClips: (id, lessonIds) => api.post(`/courses/${id}/lesson-clips`, lessonIds ? { lessonIds } : {}),
  update: (id, data) => api.put(`/courses/${id}`, data),
  delete: (id) => api.delete(`/courses/${id}`),
  publish: (id) => api.patch(`/courses/${id}/publish`),
  unpublish: (id) => api.patch(`/courses/${id}/unpublish`),

  // Modules
  createModule: (courseId, data) => api.post(`/courses/${courseId}/modules`, data),
  updateModule: (courseId, moduleId, data) => api.put(`/courses/${courseId}/modules/${moduleId}`, data),
  deleteModule: (courseId, moduleId) => api.delete(`/courses/${courseId}/modules/${moduleId}`),
  reorderModules: (courseId, moduleIds) => api.put(`/courses/${courseId}/modules/reorder`, { moduleIds }),

  // Lessons
  createLesson: (courseId, moduleId, data) => api.post(`/courses/${courseId}/modules/${moduleId}/lessons`, data),
  updateLesson: (courseId, moduleId, lessonId, data) => api.put(`/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`, data),
  deleteLesson: (courseId, moduleId, lessonId) => api.delete(`/courses/${courseId}/modules/${moduleId}/lessons/${lessonId}`),
  reorderLessons: (courseId, moduleId, lessonIds) => api.put(`/courses/${courseId}/modules/${moduleId}/lessons/reorder`, { lessonIds }),
  // Videos to record: a lesson's private on-camera script, the course's
  // recording plan, and attaching a video ({ mediaAssetId } | { youtubeUrl }).
  getLessonScript: (courseId, lessonId) => api.get(`/courses/${courseId}/lessons/${lessonId}/script`),
  getVideosToRecord: (courseId) => api.get(`/courses/${courseId}/videos-to-record`),
  attachLessonVideo: (courseId, lessonId, data) => api.post(`/courses/${courseId}/lessons/${lessonId}/attach-video`, data),

  // Student/Public
  browse: (params = {}) => api.get('/courses/browse', { params }),
  // `token` lets a guest student reach a PRIVATE course's page — it's the
  // only thing identifying them, since a guest enrolment has no user account.
  getPublic: (id, params = {}) => api.get(`/courses/${id}/public`, { params }),
  enroll: (id, data = {}) => api.post(`/courses/${id}/enroll`, data),
  getLearn: (id, params = {}) => api.get(`/courses/${id}/learn`, { params }),
  updateProgress: (id, data) => api.post(`/courses/${id}/progress`, data),
  // Same save for when the page is going away (no response is read).
  saveProgressKeepalive: (id, data) => sendCourseProgressKeepalive(id, data),
  // Worksheet answers: { answers: [{ prompt, answer }], token? }, and the
  // worksheet (with the learner's answers) as a PDF. `token` = guest link.
  saveWorksheetResponses: (id, lessonId, data) => api.put(`/courses/${id}/lessons/${lessonId}/responses`, data),
  downloadWorksheetPdf: (id, lessonId, params = {}) => api.get(`/courses/${id}/lessons/${lessonId}/worksheet.pdf`, { params, responseType: 'blob' }),
  getProgress: (id, params = {}) => api.get(`/courses/${id}/progress`, { params }),
  // Quizzes. getQuiz never includes the answers; submitQuizAttempt is scored
  // on the server and returns them with explanations. `token` = guest link.
  getQuiz: (id, lessonId, params = {}) => api.get(`/courses/${id}/lessons/${lessonId}/quiz`, { params }),
  // A 429 here is the attempt limit ({ message, retryAfter }), shown by the
  // quiz itself — so skip the client's generic wait-and-retry.
  submitQuizAttempt: (id, lessonId, data) => api.post(`/courses/${id}/lessons/${lessonId}/quiz/attempts`, data, { _rateLimitRetry: true }),
  getQuizAttempts: (id, lessonId, params = {}) => api.get(`/courses/${id}/lessons/${lessonId}/quiz/attempts`, { params }),
  // Host: write a module's quiz with AI (counts toward the hourly AI limit).
  // { courseId, moduleId, lessonId?, count?, save? } -> { questions, lesson }
  generateQuiz: (data) => api.post('/ai/course-quiz', data),
  // "Email me my access link" (public; same answer whether or not enrolled).
  requestAccessLink: (id, email) => api.post(`/courses/${id}/access-link`, { email }),
  getCreatorCourses: (userId, params = {}) => api.get(`/courses/creator/${userId}`, { params }),

  // Enrollments & Analytics
  getEnrollments: (id, params = {}) => api.get(`/courses/${id}/enrollments`, { params }),
  getAnalytics: (id) => api.get(`/courses/${id}/analytics`),

  // Checkout (paid courses)
  createCheckout: (id, data) => api.post(`/courses/${id}/checkout`, data),
};

// ===========================================
// Demo Request API (enterprise sales leads)
// ===========================================
// /coach-workshop: the "See your week" preview and the attendee-offer check.
export const coachWorkshopAPI = {
  preview: (topic) => api.post('/coach-workshop/preview', { topic }),
  checkOffer: (o) => api.get('/coach-workshop/offer', { params: { o } }),
};

export const demoRequestAPI = {
  submit: (data) => api.post('/demo-requests', data),
  list: (params) => api.get('/demo-requests', { params }),
  update: (id, data) => api.patch(`/demo-requests/${id}`, data),
};

// ===========================================
// Authenticated fetch helper
// ===========================================
// Use this instead of raw fetch() for API calls.
// Sends httpOnly cookies (credentials: 'include') AND the Bearer token
// from localStorage when available. This ensures auth works for both
// cookie-based sessions (Google OAuth) and localStorage token flows.
//
// Unlike the axios instance, this is a raw fetch — so it self-heals a 401 by
// refreshing the access token via the httpOnly refresh cookie and retrying
// once. Without this, a raw-fetch call that races ahead of auth (e.g. right
// after an OAuth redirect) would surface a stray 401 and not recover.

// Single-flight refresh shared across concurrent authFetch 401s, so a burst of
// 401s triggers exactly one /auth/refresh. Uses the httpOnly refresh cookie —
// no token is read from or sent by JS.
let authFetchRefreshPromise = null;
function refreshAccessTokenViaCookie() {
  if (!authFetchRefreshPromise) {
    authFetchRefreshPromise = axios
      .post(`${API_URL}/auth/refresh`, {}, { withCredentials: true })
      .then((res) => {
        const candidate = res?.data?.success ? res.data.accessToken : null;
        // Shape-checked for the same reason as the interceptor's write: never
        // store a value that would go back out as `Bearer undefined`.
        const newToken = isUsableAccessToken(candidate) ? candidate : null;
        if (!newToken) throw new Error('Token refresh failed');
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, newToken);
        api.defaults.headers.Authorization = `Bearer ${newToken}`;
        return newToken;
      })
      .finally(() => {
        authFetchRefreshPromise = null;
      });
  }
  return authFetchRefreshPromise;
}

export async function authFetch(url, opts = {}, _retried = false) {
  // Shape-checked: a poisoned "undefined" must never go out as a Bearer token
  // (see isUsableAccessToken). Sending no header instead yields a clean 401,
  // which the refresh below heals.
  const token = readAccessToken();
  const headers = { ...(opts.headers || {}) };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, { ...opts, credentials: 'include', headers });

  // A 401 means the request was rejected before processing, so retrying after a
  // refresh is safe for any method. Retry at most once, and never loop on the
  // auth endpoints themselves.
  if (response.status === 401 && !_retried && !url.includes('/auth/')) {
    try {
      await refreshAccessTokenViaCookie();
    } catch (e) {
      return response; // refresh failed — surface the original 401
    }
    return authFetch(url, opts, true);
  }

  return response;
}

// Export the base api instance for custom requests
export default api;
